import type { AxiosError } from "axios";
import type { ChipType } from "../../../../../components/Chips/Chips";
import type {
  ApprovalListDetailConfig,
  ApprovalWorkflow,
  ApprovalsFiltersResponse,
  ApprovalsQueryParams,
} from "../../../../../types/types.Workflow";

export type ApprovalsListVariant = "product" | "dashboard";

export type ApprovalsListFiltersState = {
  status: ChipType[];
  assignee: ChipType[];
  approver: ChipType[];
  stage: ChipType[];
  requested_by: ChipType[];
  approval_type: ChipType[];
  /** Configured list_details filters keyed by attribute key (e.g. region). */
  item_details: Record<string, ChipType[]>;
};

export const EMPTY_APPROVALS_LIST_FILTERS: ApprovalsListFiltersState = {
  status: [],
  assignee: [],
  approver: [],
  stage: [],
  requested_by: [],
  approval_type: [],
  item_details: {},
};

export function chipIds(chips: ChipType[]): string[] {
  return chips.map((chip) => String(chip.id ?? chip.name)).filter(Boolean);
}

export function buildDefaultFilters(
  user: {
    id?: string;
    firstname?: string;
    lastname?: string;
    email_address?: string;
  } | null
): ApprovalsListFiltersState {
  if (!user?.id) {
    return EMPTY_APPROVALS_LIST_FILTERS;
  }
  const name =
    [user.firstname, user.lastname].filter(Boolean).join(" ").trim() ||
    user.email_address ||
    String(user.id);
  return {
    ...EMPTY_APPROVALS_LIST_FILTERS,
    approver: [{ id: String(user.id), name, type: "user" }],
  };
}

export function isUserInApproverFilters(
  userId: string | undefined | null,
  apiFilters: ApprovalsFiltersResponse | undefined | null
): boolean {
  if (!userId || !apiFilters?.approvers?.length) {
    return false;
  }
  return apiFilters.approvers.some(
    (approver) => String(approver.id) === String(userId)
  );
}

/** True when filters are only the auto-seeded Approver=me chip. */
export function isOnlySeededApproverFilter(
  filters: ApprovalsListFiltersState,
  userId: string
): boolean {
  if (
    filters.approver.length !== 1 ||
    String(filters.approver[0]?.id) !== String(userId)
  ) {
    return false;
  }
  return (
    !filters.status.length &&
    !filters.assignee.length &&
    !filters.stage.length &&
    !filters.requested_by.length &&
    !filters.approval_type.length &&
    !Object.values(filters.item_details ?? {}).some((chips) => chips.length > 0)
  );
}

/** Map UI filter chips → API query params. */
export function toApprovalsListFilterParams(
  filters: ApprovalsListFiltersState,
  q?: string
): ApprovalsQueryParams {
  const status = chipIds(filters.status);
  const assignee_ids = chipIds(filters.assignee);
  const approver_ids = chipIds(filters.approver);
  const stage = chipIds(filters.stage);
  const requested_by_ids = chipIds(filters.requested_by);
  const approval_type = chipIds(filters.approval_type);
  const item_details = Object.entries(filters.item_details ?? {}).flatMap(
    ([key, chips]) => chipIds(chips).map((value) => `${key}:${value}`)
  );
  return {
    status: status.length ? status : undefined,
    assignee_ids: assignee_ids.length ? assignee_ids : undefined,
    approver_ids: approver_ids.length ? approver_ids : undefined,
    stage: stage.length ? stage : undefined,
    requested_by_ids: requested_by_ids.length ? requested_by_ids : undefined,
    approval_type: approval_type.length ? approval_type : undefined,
    item_details: item_details.length ? item_details : undefined,
    q: q || undefined,
  };
}

/** Keep selected chips visible in the dropdown even when API options are empty. */
export function mergeSelectedIntoOptions(
  options: ChipType[],
  selected: ChipType[] | undefined
): ChipType[] {
  const byId = new Map<string, ChipType>();
  for (const chip of [...(selected ?? []), ...options]) {
    const key = String(chip.id ?? chip.name);
    if (!byId.has(key)) {
      byId.set(key, chip);
    }
  }
  return Array.from(byId.values());
}

export type ApprovalsListTableProps = {
  variant: ApprovalsListVariant;
  approvals: ApprovalWorkflow[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
  isLoading: boolean;
  error: AxiosError | undefined;
  onRowClick: (
    approval: ApprovalWorkflow,
    e: React.MouseEvent<HTMLTableRowElement>
  ) => void;
  onPageChange: (offset: number) => void;
  onPerPageChange: (perPage: number) => void;
  perPage: number;
  offset: number;
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  searchPlaceholder?: string;
  skuIdHeader?: string;
  filters: ApprovalsListFiltersState;
  onFiltersChange: (filters: ApprovalsListFiltersState) => void;
  apiFilters?: ApprovalsFiltersResponse;
  /**
   * list_details from the list response — used to render dynamic filters/columns
   * while /filters is still loading.
   */
  listDetails?: ApprovalListDetailConfig[];
  /** Unfiltered total in scope (matching/total result label). */
  totalApprovalsCount?: number;
  /** Refresh list after an inline assignee change. */
  onAssigneeUpdated?: () => unknown;
  /** Filters restored by “Clear Filters”. Defaults to empty. */
  clearedFilters?: ApprovalsListFiltersState;
};
